/**
 * 제품 목록 검색조건 단일 원천.
 *
 * 배경(2026-08-19 운영 장애): ProductsList 의 `searchFilter` 초기값에 옵션 "값"이 아니라
 * 번역된 "라벨"(`$t('productslist_serial_no')` → "일련번호")이 들어가 있었다. 그 결과
 * 화면에는 정상 선택된 것처럼 보이지만 switch 의 어느 case 와도 맞지 않아
 *   - 검색 파라미터가 실리지 않고
 *   - `searchFilter && condition` 은 참이라 날짜 필터까지 건너뛰어
 * 55만 건 전체 목록이 "검색 결과"로 표시됐다. 드롭다운을 직접 한 번 고른 뒤에는 정상
 * 동작해 오래 잠복했다.
 *
 * 그래서 값·라벨키·백엔드 파라미터명을 한 곳에 묶고, 화면은 이 정의에서만 파생시킨다.
 * 라벨은 labelKey 로만 존재하므로 "라벨을 값으로 쓰는" 실수가 구조적으로 불가능하다.
 * 로케일을 바꿔도(zh: 序列号) 값은 불변이다.
 */

/** @typedef {{ value: string, labelKey: string, param: string }} ProductSearchFilter */

/**
 * 검색조건 정의. `param` 은 china-inapi GET /products/admin/dentium/china 가 받는 쿼리명이다
 * (admin-product.controller.ts). 백엔드가 받지 않는 이름을 넣으면 조용히 무시되어
 * 전체 목록이 나오므로, 여기 값을 바꿀 때는 컨트롤러와 함께 확인해야 한다.
 * @type {ProductSearchFilter[]}
 */
export const PRODUCT_SEARCH_FILTERS = [
  { value: 'serialNo', labelKey: 'productslist_serial_no', param: 'serialNo' },
  { value: 'productCode', labelKey: 'productslist_product_code', param: 'itemNo' },
];

/** 화면 진입 시 선택되는 기본 검색조건 (반드시 정의에서 파생 — 리터럴 재입력 금지). */
export const DEFAULT_PRODUCT_SEARCH_FILTER = PRODUCT_SEARCH_FILTERS[0].value;

/** 주어진 값이 실제 검색조건 값인지 판정한다. URL 쿼리 유입값 검증에 쓴다. */
export function isValidProductSearchFilter(value) {
  return PRODUCT_SEARCH_FILTERS.some((f) => f.value === value);
}

/**
 * 검색 파라미터를 만든다. 만들 수 없으면 null 을 돌려준다.
 *
 * null 을 받은 호출부는 "검색 없음"으로 취급해 날짜 필터를 그대로 적용해야 한다.
 * 검색이 성립하지 않는데 날짜 필터까지 빠지면 전체 목록이 검색 결과처럼 보이기 때문이다.
 *
 * @param {string} searchFilter 검색조건 값
 * @param {string} condition 검색어(앞뒤 공백은 제거된다)
 * @returns {Record<string, string> | null}
 */
export function buildProductSearchParams(searchFilter, condition) {
  const keyword = (condition || '').trim();
  if (!keyword) return null;

  const filter = PRODUCT_SEARCH_FILTERS.find((f) => f.value === searchFilter);
  if (!filter) return null;

  return { [filter.param]: keyword };
}
